import request from './request'
import { API_ENDPOINTS } from './config'

const shopAPI = {
    // ==================== 分类相关API ====================

    // 获取分类列表（平面结构，向后兼容）
    getCategories() {
        return request({
            url: API_ENDPOINTS.CATEGORIES.ACTIVE,
            method: 'get'
        }).then(response => {
            return response
        }).catch(error => {
            console.error('API错误', API_ENDPOINTS.CATEGORIES.ACTIVE + ':', error)
            throw error
        })
    },

    // 获取分类树形结构（包含所有层级和商品数量）
    getCategoryTree() {
        return request({
            url: API_ENDPOINTS.CATEGORIES.TREE,
            method: 'get'
        }).then(response => {
            return response
        }).catch(error => {
            console.error('分类树API错误:', error)
            throw error
        })
    },

    // 获取顶级分类
    getTopLevelCategories() {
        return request({
            url: API_ENDPOINTS.CATEGORIES.TOP_LEVEL,
            method: 'get'
        })
    },

    // 根据父分类ID获取子分类
    getCategoriesByParentId(parentId) {
        return request({
            url: API_ENDPOINTS.CATEGORIES.CHILDREN(parentId),
            method: 'get'
        })
    },

    // 根据层级获取分类
    getCategoriesByLevel(level) {
        return request({
            url: API_ENDPOINTS.CATEGORIES.BY_LEVEL(level),
            method: 'get'
        })
    },

    // 获取指定分类的所有子分类（递归）
    getChildrenCategories(categoryId) {
        return request({
            url: API_ENDPOINTS.CATEGORIES.DESCENDANTS(categoryId),
            method: 'get'
        })
    },

    // ==================== 商品相关API ====================

    // 根据分类ID获取商品列表
    getProductsByCategory(categoryId) {
        return request({
            url: API_ENDPOINTS.PRODUCTS.BY_CATEGORY(categoryId),
            method: 'get'
        })
    },

    // 获取热门商品
    getHotProducts(limit = 8) {
        return request({
            url: API_ENDPOINTS.PRODUCTS.HOT,
            method: 'get',
            params: { limit }
        })
    },

    // 获取新品商品
    getNewProducts(limit = 8) {
        return request({
            url: API_ENDPOINTS.PRODUCTS.NEW,
            method: 'get',
            params: { limit }
        })
    },

    // 根据标签搜索商品
    searchProducts(tag, options = {}) {
        return request({
            url: API_ENDPOINTS.PRODUCTS.SEARCH,
            method: 'get',
            params: { tag },
            ...options // 支持传入AbortController等选项
        })
    },

    // 获取商品详情
    getProduct(productId) {
        return request({
            url: API_ENDPOINTS.PRODUCTS.DETAIL(productId),
            method: 'get'
        })
    },

    // 获取公告列表
    getNotices() {
        return request({
            url: API_ENDPOINTS.NOTICES.ACTIVE,
            method: 'get'
        })
    },

    // 获取置顶公告
    getTopNotices() {
        return request({
            url: API_ENDPOINTS.NOTICES.TOP,
            method: 'get'
        })
    },

    // 获取店铺设置
    getSettings() {
        return request({
            url: API_ENDPOINTS.SETTINGS.ALL,
            method: 'get'
        })
    },

    // 根据设置键获取设置值
    getSetting(settingKey) {
        return request({
            url: API_ENDPOINTS.SETTINGS.BY_KEY(settingKey),
            method: 'get'
        })
    },

    // 获取商品演示图片
    getProductDemoImages(productId) {
        return request({
            url: API_ENDPOINTS.PRODUCTS.DEMO_IMAGES(productId),
            method: 'get'
        })
    },

    // 同步商品图片到数据库
    syncProductImages(productId, imageData) {
        return request({
            url: API_ENDPOINTS.GALLERY.ADD,
            method: 'post',
            data: {
                productId: productId,
                imageUrl: imageData.url,
                imageTitle: imageData.title || '',
                imageDescription: imageData.description || '',
                imageCategory: imageData.category || 'demo',
                imageType: 'image',
                isFeatured: imageData.featured ? '1' : '0',
                isCover: imageData.cover ? '1' : '0',
                sortOrder: imageData.sortOrder || 0,
                status: '0'
            }
        }).then(response => {
            return response
        }).catch(error => {
            console.error('图片同步失败:', error)
            throw error
        })
    },

    // 批量同步商品图片
    batchSyncProductImages(productId, imagesData) {
        const promises = imagesData.map(imageData =>
            this.syncProductImages(productId, imageData)
        )
        return Promise.allSettled(promises)
    },

    // 上传单张商品图片
    uploadProductImage(file) {
        const formData = new FormData()
        formData.append('file', file)
        return request({
            url: API_ENDPOINTS.PRODUCTS.UPLOAD_IMAGE,
            method: 'post',
            data: formData,
            headers: {
                'Content-Type': 'multipart/form-data'
            }
        })
    },

    // 批量上传商品图片
    uploadProductImages(files) {
        const formData = new FormData()
        files.forEach(file => {
            formData.append('files', file)
        })
        return request({
            url: API_ENDPOINTS.PRODUCTS.UPLOAD_IMAGES,
            method: 'post',
            data: formData,
            headers: {
                'Content-Type': 'multipart/form-data'
            }
        })
    },

    // 删除商品图片
    deleteProductImage(fileName) {
        return request({
            url: API_ENDPOINTS.PRODUCTS.DELETE_IMAGE,
            method: 'delete',
            params: { fileName }
        })
    },

    // 压缩上传商品图片
    uploadCompressedImage(file, options = {}) {
        const formData = new FormData()
        formData.append('file', file)
        if (options.quality) formData.append('quality', options.quality)
        if (options.maxWidth) formData.append('maxWidth', options.maxWidth)
        if (options.maxHeight) formData.append('maxHeight', options.maxHeight)

        return request({
            url: API_ENDPOINTS.PRODUCTS.UPLOAD_COMPRESSED,
            method: 'post',
            data: formData,
            headers: {
                'Content-Type': 'multipart/form-data'
            }
        })
    },

    // ==================== 产品画廊相关API ====================

    // 获取产品画廊列表
    getProductGallery(productId) {
        return request({
            url: API_ENDPOINTS.GALLERY.BY_PRODUCT(productId),
            method: 'get'
        }).then(response => {
            return response
        }).catch(error => {
            console.error('画廊API错误:', error)
            throw error
        })
    },

    // 获取特色画廊图片
    getFeaturedGallery(productId, limit = null) {
        const params = limit ? { limit } : {}
        return request({
            url: API_ENDPOINTS.GALLERY.FEATURED(productId),
            method: 'get',
            params
        })
    },

    // 获取封面图片
    getCoverImage(productId) {
        return request({
            url: API_ENDPOINTS.GALLERY.COVER(productId),
            method: 'get'
        })
    },

    // 根据分类获取画廊
    getGalleryByCategory(productId, category) {
        return request({
            url: API_ENDPOINTS.GALLERY.BY_CATEGORY(productId, category),
            method: 'get'
        })
    },

    // 获取画廊统计信息
    getGalleryStats(productId) {
        return request({
            url: API_ENDPOINTS.GALLERY.STATS(productId),
            method: 'get'
        })
    },

    // 获取画廊详情
    getGalleryDetail(galleryId) {
        return request({
            url: API_ENDPOINTS.GALLERY.DETAIL(galleryId),
            method: 'get'
        })
    },

    // 新增画廊图片
    addGalleryImage(galleryData) {
        return request({
            url: API_ENDPOINTS.GALLERY.ADD,
            method: 'post',
            data: galleryData
        })
    },

    // 更新画廊图片
    updateGalleryImage(galleryData) {
        return request({
            url: API_ENDPOINTS.GALLERY.UPDATE,
            method: 'put',
            data: galleryData
        })
    },

    // 删除画廊图片
    deleteGalleryImages(galleryIds) {
        const ids = Array.isArray(galleryIds) ? galleryIds.join(',') : galleryIds
        return request({
            url: API_ENDPOINTS.GALLERY.DELETE(ids),
            method: 'delete'
        })
    },

    // 更新画廊排序
    updateGallerySort(galleries) {
        return request({
            url: API_ENDPOINTS.GALLERY.UPDATE_SORT,
            method: 'put',
            data: galleries
        })
    },

    // 设置封面图片
    setCoverImage(productId, galleryId) {
        return request({
            url: API_ENDPOINTS.GALLERY.SET_COVER(productId, galleryId),
            method: 'put'
        })
    },

    // 取消封面图片
    unsetCoverImage(productId) {
        return request({
            url: API_ENDPOINTS.GALLERY.UNSET_COVER(productId),
            method: 'delete'
        })
    },

    // 切换特色状态
    toggleFeaturedStatus(galleryId) {
        return request({
            url: API_ENDPOINTS.GALLERY.TOGGLE_FEATURED(galleryId),
            method: 'put'
        })
    },

    // 批量导入画廊图片
    batchImportGallery(productId, imageUrls) {
        return request({
            url: API_ENDPOINTS.GALLERY.BATCH_IMPORT(productId),
            method: 'post',
            data: { imageUrls }
        })
    },

    // 复制画廊到其他商品
    copyGalleryToProduct(sourceProductId, targetProductId) {
        return request({
            url: API_ENDPOINTS.GALLERY.COPY_GALLERY(sourceProductId, targetProductId),
            method: 'post'
        })
    },

    // 设置画廊标签
    setGalleryTags(galleryId, tagIds) {
        return request({
            url: API_ENDPOINTS.GALLERY.SET_TAGS(galleryId),
            method: 'put',
            data: { tagIds }
        })
    },

    // 添加画廊标签
    addGalleryTags(galleryId, tagIds) {
        return request({
            url: API_ENDPOINTS.GALLERY.ADD_TAGS(galleryId),
            method: 'post',
            data: { tagIds }
        })
    },

    // 移除画廊标签
    removeGalleryTags(galleryId, tagIds) {
        return request({
            url: API_ENDPOINTS.GALLERY.REMOVE_TAGS(galleryId),
            method: 'delete',
            data: { tagIds }
        })
    }
}

export default shopAPI
